import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { CalendarDateComponent } from "src/app/shared/components/calendar-date/calendar-date.component";
import { IconComponent } from "src/app/shared/components/icon/icon.component";
import { DateComponent } from "src/app/shared/components/inputs/simple/date/date.component";
import { PipesModule } from "../../../../shared/pipes/pipe-module";
import { CommonModule } from '@angular/common';
import { LayoutService } from 'src/app/core/services/layout.service';
import { DialogData, IntervalsLoaded, IntervalView, MarkView, RawMark } from './interface/interfaces';
import { localDB } from 'src/app/shared/helpers/localStorage';
import { encriptar } from 'src/app/shared/helpers/encryption';
import { environment as env } from 'src/environments/environment';
import { MarksHelpComponent } from "../interval-marks/marks-help/marks-help.component";
import { IntervalListComponent } from "./component/interval-list/interval-list.component";
import { debounceTime, startWith, take } from 'rxjs';
import { MarkingService } from 'src/app/shared/components/marking/service/marking.service';
import { NewRequestExtempComponent } from './component/new-request-extemp/new-request-extemp.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CollaboratorMarksRecomendedWrapperResponse } from 'src/app/shared/components/marking/interfaces/marks-recommended.interface';
import { buildRecommendedMarkPairs } from 'src/app/shared/components/marking/helpers/marks-recommended.helper';
import { calculateActiveHours } from './helpers/helper';
import { AttendanceService } from 'src/app/modules/core/attendance/service/attendance.service';
import { MARKS_STATUS } from './helpers/enums';
import { AccessService } from 'src/app/core/services/access.service';

@Component({
  standalone: true,
  selector: 'mg-trademark-regularization',
  templateUrl: './trademark-regularization.component.html',
  styleUrls: ['./trademark-regularization.component.scss'],
  imports: [
    CalendarDateComponent,
    IconComponent,
    DateComponent,
    PipesModule,
    CommonModule,
    MarksHelpComponent,
    IntervalListComponent,
    MatExpansionModule,
    MatTooltipModule,
    NewRequestExtempComponent
  ]
})
export class TrademarkRegularizationComponent implements OnInit {
  request: FormGroup;
  totalMarcaciones: number = 0;
  totalPendientes: number = 0;
  totalAprobadas: number = 0;
  nId_Collaborator: number
  intervals: IntervalView[] = [];
  selectedDate: Date = moment().toDate();
  minDate: Date = moment().subtract(2, 'years').toDate();
  maxDate: Date = moment().endOf('day').toDate();
  closingDate: Date | null = null;
  closingDate_request: Date;
  disabledDays: string[] = [];
  loadingCalendar: boolean = true;
  fechasIncompletas: string[] = [];
  fechasSinMarcacion: string[] = [];
  fechasConSolicitudPendiente: string[] = [];
  fechaCierreSerie: string[] = [];
  marksHintText: string = '';
  horasActivas: string = '0h 0m';
  marksIntervals: { inicio: string; fin: string }[] = [];
  hasIntervals: boolean = true;
  isLoadingIntervals: boolean = false;
  showHelp: boolean = false;
  showNewRequest: boolean = false;
  selectDay: Date | null = null;
  isMarksTimeActive: boolean = false;
  hasPendingRegularization: boolean = false;
  pendingRequestId: number | null = null;
  isLoadingIntervalList: boolean = true;
  shakeBadge: boolean = false;
  hasAttendanceForSelectedDate: boolean = true;
  isCheckingAttendance: boolean = false;

  public isScreenMD$ = this._layoutService.isScreenMD$;

  get selectedDateLabel(): string {
    const fecha = this.request.get('dFecha_Inicio')?.value;
    if (!fecha) return '';
    return moment(fecha).locale('es').format('dddd, D [de] MMMM [de] YYYY');
  }

  get isNewRequestDisabled(): boolean {
    if (!this._accessService.validarPermisoSlug('MARKS-CRUD-MODAL')) return true;
    if (this.isLoadingIntervalList || this.isCheckingAttendance) return true;
    if (!this.hasAttendanceForSelectedDate) return true;
    if (this.hasPendingRegularization) return true;
    if (!this.isMarksTimeActive) return false;
    if (!this.closingDate || !this.selectedDate) return false;
    return moment(this.selectedDate).isBefore(this.closingDate, 'day');
  }

  get isBeforeClosingDate(): boolean {
    return this.isMarksTimeActive && !!this.closingDate && !!this.selectedDate &&
      moment(this.selectedDate).isBefore(this.closingDate, 'day');
  }

  get newRequestDisabledTooltip(): string {
    if (this.isLoadingIntervalList || this.isCheckingAttendance) return '';
    if (!this.hasAttendanceForSelectedDate) return 'No cuentas con asistencia registrada para esta fecha.';
    if (this.isBeforeClosingDate) return 'Fecha anterior al cierre de planilla.';
    if (this.hasPendingRegularization) return 'Tiene una solicitud de regularización de marcas pendiente.';
    return '';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public _dialogRef: MatDialogRef<TrademarkRegularizationComponent>,
    private readonly _layoutService: LayoutService,
    private readonly _marksService: MarkingService,
    private readonly _attendanceService: AttendanceService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _accessService: AccessService,
  ) { }

  ngOnInit(): void {
    this.nId_Collaborator = Number(localDB.getItem('idColaborador'));
    this.processClosingDate()
    this.initForm();
    this.listenForRecommendedMarks();
  }

  private processClosingDate(): void {
    const configs = (this.data as any)?.CLOSING_DATE || [];
    const marksTimeConfig = (this.data as any)?.MARKS_TIME || [];
    this.isMarksTimeActive = false;
    if (marksTimeConfig.length > 0) {
      const desc = String(marksTimeConfig[0]?.sDescripcion ?? '').trim();
      this.isMarksTimeActive = desc === '1';
    }
    if (configs.length > 0) {
      const maxConfig = configs.reduce((prev: any, current: any) => {
        return Number(current.sCodigo) > Number(prev.sCodigo) ? current : prev;
      }, configs[0]);
      if (maxConfig?.sDescripcion) {
        this.closingDate = moment(maxConfig.sDescripcion, 'YYYY-MM-DD').startOf('day').toDate();

        // Resaltar todas las fechas disponibles en configs (preferiblemente CLOSING_DATE_MARKS)
        this.fechaCierreSerie = configs
          .map((c: any) => moment(c.sDescripcion).format('YYYY-MM-DD'))
          .filter((d: string) => !!d);

        if (this.isMarksTimeActive) {
          this.closingDate_request = moment(this.closingDate).toDate();
        } else {
          this.closingDate_request = moment().subtract(2, 'years').toDate();
        }
        this.fetchMissingMarksStatus(maxConfig.sDescripcion);
      }
    }
  }

  private fetchMissingMarksStatus(closureDateStr: string): void {
    if (!this.nId_Collaborator || !closureDateStr) return;
    this.loadingCalendar = true;
    this._attendanceService.getMissingMarksStatus(closureDateStr, this.nId_Collaborator)
      .subscribe({
        next: (response: any) => {
          if (response.isSuccess) {
            const data = response.data;
            this.fechasIncompletas = data
              .filter((d: any) => d.sEstado_Marcacion === MARKS_STATUS.MARCAS_INCOMPLETAS)
              .map((d: any) => moment(d.dFecha_Jornada).format('YYYY-MM-DD'));

            this.fechasSinMarcacion = data
              .filter((d: any) => d.sEstado_Marcacion === MARKS_STATUS.SIN_MARCACION)
              .map((d: any) => moment(d.dFecha_Jornada).format('YYYY-MM-DD'));

            this.fechasConSolicitudPendiente = data
              .filter((d: any) => d.bTiene_Solicitud_Pendiente)
              .map((d: any) => moment(d.dFecha_Jornada).format('YYYY-MM-DD'));

            this.disabledDays = this.generateDisabledDays();

            this.loadingCalendar = false;
            this._cdr.detectChanges();
          } else {
            console.warn('Mensaje:', response.message);
            this.loadingCalendar = false;
            this._cdr.detectChanges();
          }
        },
        error: (err: any) => {
          console.error('Error:', err);
          this.loadingCalendar = false;
          this._cdr.detectChanges();
        }
      });
  }

  private generateDisabledDays(): string[] {
    const disabled = new Set<string>();

    if (this.isMarksTimeActive && this.closingDate) {
      let currentDate = (moment as any)(this.minDate).startOf('day');
      const closing = (moment as any)(this.closingDate).startOf('day');
      while (currentDate.isBefore(closing, 'day')) {
        disabled.add(currentDate.format('YYYY-MM-DD'));
        currentDate.add(1, 'day');
      }
    }

    this.fechasConSolicitudPendiente.forEach(fecha => disabled.add(fecha));

    return Array.from(disabled);
  }

  initForm(): void {
    const fechaInicial = this.data.dFechaMarcacion ? moment(this.data.dFechaMarcacion).toDate() : moment().toDate();
    this.selectedDate = fechaInicial;
    this.selectDay = fechaInicial;

    this.request = new FormGroup({
      nId_Colaborador: new FormControl(null),
      nId_Tipo_Solicitud: new FormControl(null),
      dFecha_Inicio: new FormControl(fechaInicial, Validators.required),
      dFecha_Fin: new FormControl(null),
      dHora_Inicio: new FormControl(null),
      dHora_Fin: new FormControl(null),
      nCantidad: new FormControl(null),
      nId_Tipo_Motivo: new FormControl(17),
      sMotivo: new FormControl(null),
      aFiles: new FormArray([]),
      nPeriodo: new FormControl(null),
    });
  }

  listenForRecommendedMarks(): void {
    const fechaInicial = this.request.get('dFecha_Inicio')?.value;
    this.request.get('dFecha_Inicio')?.valueChanges
      .pipe(
        startWith(fechaInicial),
        debounceTime(300)
      )
      .subscribe((fecha: Date) => {
        if (!fecha) return;
        this.selectedDate = moment(fecha).toDate();
        const formatted = moment(fecha).format('YYYY-MM-DD');
        this._marksService.getMarksRecomended(formatted).pipe(take(1)).subscribe({
          next: (resp) => {
            if (resp.isSuccess && resp.data) {
              this.setRecommendedHint(resp.data);
            } else {
              this.marksHintText = '';
              this.marksIntervals = [];
            }
          },
          error: () => {
            this.marksHintText = '';
            this.marksIntervals = [];
          }
        });
        this.checkAttendanceForSelectedDate(formatted);
      });
  }

  private checkAttendanceForSelectedDate(fecha: string): void {
    if (!this.nId_Collaborator) return;
    this.isCheckingAttendance = true;
    this._attendanceService.hasAttendance(this.nId_Collaborator, fecha).pipe(take(1)).subscribe({
      next: (resp) => {
        this.hasAttendanceForSelectedDate = resp.isSuccess && !!resp.data;
        this.isCheckingAttendance = false;
        this._cdr.detectChanges();
      },
      error: () => {
        this.hasAttendanceForSelectedDate = false;
        this.isCheckingAttendance = false;
        this._cdr.detectChanges();
      }
    });
  }

  private setRecommendedHint(data: CollaboratorMarksRecomendedWrapperResponse): void {
    const pares = buildRecommendedMarkPairs(data);
    this.marksIntervals = pares;
    this.marksHintText = pares.length > 0 ? 'Estas son tus marcas recomendadas' : '';
  }

  buildIntervals(rawMarks: RawMark[]): void {
    if (rawMarks?.length) {
      const grouped = rawMarks.reduce(
        (acc: Record<number, { inicio?: RawMark; fin?: RawMark }>, mark) => {
          if (!acc[mark.nInterval]) acc[mark.nInterval] = {};
          if (mark.nType_Mark === 1) acc[mark.nInterval].inicio = mark;
          if (mark.nType_Mark === 2) acc[mark.nInterval].fin = mark;
          return acc;
        }, {}
      );
      this.intervals = Object.values(grouped)
        .map(group => ({
          nInterval: group.inicio?.nInterval ?? group.fin?.nInterval ?? 0,
          inicio: group.inicio ? this.toMarkView(group.inicio) : null,
          fin: group.fin ? this.toMarkView(group.fin) : null,
        }))
        .sort((a, b) => {
          const fechaA = a.inicio?.dTiempo_Marca ?? a.fin?.dTiempo_Marca;
          const fechaB = b.inicio?.dTiempo_Marca ?? b.fin?.dTiempo_Marca;
          if (!fechaA || !fechaB) return 0;
          return fechaA.getTime() - fechaB.getTime();
        });
    } else {
      this.intervals = [];
    }
  }

  private toMarkView(mark: RawMark): MarkView {
    return {
      nId: mark.nId,
      dTiempo_Marca: new Date(mark.dDate),
      sIcono_Marcaje: mark.sIcono_Marcaje,
      sDispositivo: this.resolveDispositivo(mark.sMetodo_Marcaje),
      bEsPendiente: !!mark.nId_Solicitud,
      bIs_Extemporanea: mark.bIs_Extemporanea === 1,
      lat: null,
      lon: null,
      nEstado_Solicitud: mark.nEstado_Solicitud,
    };
  }

  private resolveDispositivo(metodo: string): string {
    const map: Record<string, string> = {
      'WEB-DESKTOP': '',
      'MOBILE': '',
      'BIOMETRICO': '',
      'HUELLA': '',
      'TABLET': '',
    };
    return map[metodo?.toUpperCase()] ?? metodo ?? 'Desconocido';
  }

  onIntervalsLoaded(event: IntervalsLoaded): void {
    this.hasIntervals = event.intervals.length > 0;
    this.totalMarcaciones = event.totalMarcaciones;
    this.totalPendientes = event.totalPendientes;
    this.totalAprobadas = event.totalAprobadas || 0;
    this.horasActivas = calculateActiveHours(event.intervals);
  }

  onPendingRegularizationChange(event: { hasPending: boolean; nId_Solicitud: number | null }): void {
    this.hasPendingRegularization = event.hasPending;
    this.pendingRequestId = event.nId_Solicitud;
    if (event.hasPending) this.triggerShake();
    this._cdr.detectChanges();
  }

  goToPendingRequest(): void {
    if (!this.pendingRequestId) return;
    const params: Record<string, string> = {
      solicitud: String(this.pendingRequestId),
      dFecha: (moment as any)(this.selectedDate).format('YYYY-MM-DD'),
    };
    const queryString = Object.entries(params)
      .map(([key, value]) =>
        `${encodeURIComponent(encriptar(env.securityKey, key))}=${encodeURIComponent(encriptar(env.securityKey, value))}`
      )
      .join('&');
    window.open(`${window.location.origin}/solicitudes?${queryString}`, '_blank');
  }

  private triggerShake(): void {
    this.shakeBadge = false;
    this._cdr.detectChanges();
    setTimeout(() => {
      this.shakeBadge = true;
      this._cdr.detectChanges();
      setTimeout(() => {
        this.shakeBadge = false;
        this._cdr.detectChanges();
      }, 600);
    }, 10);
  }

  onIntervalListLoadingChange(isLoading: boolean): void {
    this.isLoadingIntervalList = isLoading;
    this._cdr.detectChanges();
  }

  selectDate(e: Date): void {
    if (!e) return;
    this.request.markAsUntouched();
    this.request.get('dFecha_Inicio')!.patchValue(moment(e).toDate());
    this.selectedDate = moment(e).toDate();
    this.selectDay = moment(e).toDate();
    this.hasPendingRegularization = false;
    this.pendingRequestId = null;
    if (this.isBeforeClosingDate) this.triggerShake();
  }

  loadCalendar() {
    this.showNewRequest = false
    this.processClosingDate()
  }
}